import React from 'react';
import apiService from '../../services/shared/apiService';

interface ReportDownloadProps {
  latestReport: any;
  userId: string;
}

const ReportDownload: React.FC<ReportDownloadProps> = ({ latestReport, userId }) => {
  const handleDownload = async (reportId: string) => {
    try {
      const response = await apiService.downloadReport(reportId);
      // Create a blob and download
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mentra-report-${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Failed to download report. Please try again.');
    }
  };

  const generateNewReport = async () => {
    try {
      await apiService.generateWeeklyReport();
      alert('Report generation started. Check back in a few minutes.');
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="text-2xl mr-3">📋</div>
          <h3 className="text-lg font-semibold">Weekly Reports</h3>
        </div>
        <button
          onClick={generateNewReport}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors"
        >
          Generate New Report
        </button>
      </div>

      {latestReport ? (
        <div className="space-y-4">
          <div className="bg-gray-50  p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">Latest Report</h4>
                <p className="text-sm text-gray-500 ">
                  Generated on {new Date(latestReport.generatedAt).toLocaleDateString()}
                </p>
                {latestReport.reportType && (
                  <p className="text-sm text-gray-500 ">
                    Type: {latestReport.reportType}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleDownload(latestReport.id)}
                className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-sm transition-colors"
              >
                Download PDF
              </button>
            </div>
          </div>

          {latestReport.summary && (
            <div className="bg-blue-50  p-4 rounded-lg">
              <h4 className="font-medium mb-2">Report Summary</h4>
              <p className="text-sm text-gray-700 ">
                {latestReport.summary}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="text-3xl mb-3">📄</div>
          <h4 className="text-lg font-medium mb-2">No Reports Available</h4>
          <p className="text-gray-500  mb-4">
            Generate your first weekly report to see detailed insights about your mental health journey.
          </p>
          <button
            onClick={generateNewReport}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            Generate First Report
          </button>
        </div>
      )}

      <div className="mt-6 p-4 bg-yellow-50  rounded-lg">
        <div className="flex items-start space-x-2">
          <div className="text-yellow-600  text-lg">💡</div>
          <div className="text-sm text-yellow-800 ">
            <strong>Tip:</strong> Weekly reports include your symbolic trends, radar chart changes, 
            and AI-generated insights. You can share these with your therapist or keep them for personal reflection.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDownload; 